import apiClient from '../api/client';
import {
  ApiResponse,
  SuperAdminProfile,
  Admin,
  CreateAdminRequest,
  UpdateAdminRequest,
  UpdateProfileRequest,
  ChangePasswordRequest,
  UpdateAdminStatusRequest,
  DeleteAdminRequest,
} from '../../types/api.types';

class SuperAdminService {
  // ========================================
  // PROFILE MANAGEMENT
  // ========================================

  /**
   * Get super admin profile
   * GET /super-admin/profile
   */
  async getProfile(): Promise<SuperAdminProfile> {
    const response = await apiClient.get<ApiResponse<SuperAdminProfile>>('/super-admin/profile');
    return response.data.data;
  }

  /**
   * Update super admin profile (with file upload)
   * POST /super-admin/update-profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<SuperAdminProfile> {
    const formData = new FormData();
    
    if (data.full_name) formData.append('full_name', data.full_name);
    if (data.mobile_number) formData.append('mobile_number', data.mobile_number);
    if (data.file) formData.append('file', data.file);

    const response = await apiClient.post<ApiResponse<SuperAdminProfile>>(
      '/super-admin/update-profile',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  /**
   * Change password
   * POST /super-admin/change-password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await apiClient.post<ApiResponse<null>>('/super-admin/change-password', data);
  }

  // ========================================
  // ADMIN MANAGEMENT
  // ========================================

  /**
   * Get all admins
   * GET /super-admin/admins
   */
  async getAllAdmins(): Promise<Admin[]> {
    const response = await apiClient.get<ApiResponse<Admin[]>>('/super-admin/admins');
    return response.data.data;
  }

  /**
   * Get admin by ID
   * GET /super-admin/admins/:admin_id
   */
  async getAdminById(adminId: number): Promise<Admin> {
    const response = await apiClient.get<ApiResponse<Admin>>(`/super-admin/admins/${adminId}`);
    return response.data.data;
  }

  /**
   * Create new admin
   * POST /super-admin/admins/create
   */
  async createAdmin(data: CreateAdminRequest): Promise<void> {
    await apiClient.post<ApiResponse<null>>('/super-admin/admins/create', data);
  }

  /**
   * Update admin (with optional file upload)
   * POST /super-admin/admins/update
   */
  async updateAdmin(data: UpdateAdminRequest): Promise<Admin> {
    const formData = new FormData();
    
    formData.append('admin_id', data.admin_id.toString());
    if (data.full_name) formData.append('full_name', data.full_name);
    if (data.email) formData.append('email', data.email);
    if (data.mobile_number) formData.append('mobile_number', data.mobile_number);
    if (data.password) formData.append('password', data.password);
    if (data.file) formData.append('file', data.file);

    const response = await apiClient.post<ApiResponse<Admin>>(
      '/super-admin/admins/update',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  /**
   * Update admin status (activate/deactivate)
   * POST /super-admin/admins/update-status
   */
  async updateAdminStatus(data: UpdateAdminStatusRequest): Promise<void> {
    await apiClient.post<ApiResponse<null>>('/super-admin/admins/update-status', data);
  }

  /**
   * Delete admin
   * POST /super-admin/admins/delete
   */
  async deleteAdmin(data: DeleteAdminRequest): Promise<void> {
    await apiClient.post<ApiResponse<null>>('/super-admin/admins/delete', data);
  }
}

export default new SuperAdminService();
